import { useCallback, useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { bulkDelete } from "./_shared";

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const reload = useCallback(
    () => adminApi().get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {}),
    [],
  );
  useEffect(() => { reload(); }, [reload]);

  const deleteOne = async (r) => {
    if (!window.confirm("Move this review to Recently Deleted?\n\nYou can restore it within 30 days from the Recently Deleted tab.")) return;
    await adminApi().delete(`/admin/reviews/${r.id}`);
    reload();
    toast.success("Review moved to Recently Deleted");
  };

  return (
    <div className="mt-6 space-y-2" data-testid="admin-reviews-tab">
      {reviews.length === 0 ? (
        <p className="text-[#5C5555]">No reviews yet.</p>
      ) : (
        <div className="flex items-center justify-between flex-wrap gap-2 bg-[#F2E8E4]/50 border border-[#E5DBD8] px-3 py-2 rounded mb-3">
          <label className="inline-flex items-center gap-2 text-sm font-hand cursor-pointer">
            <input
              type="checkbox"
              checked={selected.size > 0 && selected.size === reviews.length}
              onChange={(e) => setSelected(e.target.checked ? new Set(reviews.map((r) => r.id)) : new Set())}
              data-testid="reviews-select-all"
              className="w-4 h-4 accent-[#7A1F2B] cursor-pointer"
            />
            Select all
          </label>
          <div className="text-xs text-[#5C5555]">{selected.size} selected</div>
          <div className="flex gap-2">
            {selected.size > 0 && (
              <button type="button" onClick={() => setSelected(new Set())} className="text-xs px-3 py-1.5 border border-[#E5DBD8] hover:bg-white rounded">Clear</button>
            )}
            <button
              type="button"
              disabled={selected.size === 0}
              onClick={() => bulkDelete("/admin/reviews/delete-bulk", Array.from(selected), reload, setSelected, "reviews", true)}
              data-testid="reviews-bulk-delete"
              className="text-xs px-3 py-1.5 bg-[#7A1F2B] text-white rounded hover:bg-[#5C1520] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
            >
              <Trash2 className="w-3.5 h-3.5"/> Delete selected
            </button>
          </div>
        </div>
      )}
      {reviews.map((r) => {
        const checked = selected.has(r.id);
        return (
          <div key={r.id} className={`flex gap-3 bg-white border p-4 ${checked ? "border-[#7A1F2B]" : "border-[#E5DBD8]"}`}>
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setSelected((prev) => { const n = new Set(prev); if (e.target.checked) n.add(r.id); else n.delete(r.id); return n; })}
              data-testid={`review-check-${r.id}`}
              className="mt-1 w-4 h-4 accent-[#7A1F2B] cursor-pointer shrink-0"
            />
            <div className="flex-1">
              <p className="font-serif text-lg">{r.name} <span className="text-sm text-[#7A1F2B]">{"★".repeat(r.rating)}</span></p>
              <p className="text-sm text-[#5C5555] mt-1">{r.comment}</p>
              <p className="text-xs text-[#5C5555] mt-1">Product ID: {r.product_id.slice(0, 8)} · {new Date(r.created_at).toLocaleString()}</p>
            </div>
            <button
              onClick={() => deleteOne(r)}
              data-testid={`admin-delete-review-${r.id}`}
              className="text-[#7A1F2B] hover:text-[#5C1520] self-start"
            ><Trash2 className="w-4 h-4"/></button>
          </div>
        );
      })}
    </div>
  );
}
